/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @version 1.14.0
 * @since 1.14.0
 */

import { Fragment, useCallback } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Checkbox, LinkBlock, RadioButton } from '../../../components/common';
import { ELinkTarget } from '../../../components/common/ExternalLink/ExternalLink';
import { makeSelectTranslationWizardAdditionalAccounts } from '../../../modules/internationalization/intSelectors';
import { IWizardAdditionalAccountsTranslation } from '../../../modules/internationalization/types/wizards/additionalAccounts';
import { IRootReducer } from '../../../reducers/rootReducer';
import { TWizardAdditionalAccountsSetParamsPayload } from '../types/wizardAdditionalAccountsActionTypes';
import {
    wizardAdditionalAccountsSetParamsAction,
} from '../wizardAdditionalAccountsActions';
import { EWizardAdditionalAccountsSteps } from '../wizardAdditionalAccountsConstant';
import { IWizardAdditionalAccountsReducer } from '../wizardAdditionalAccountsReducer';
import {
    makeSelectWizardAdditionalAccountsActivateTypes,
    makeSelectWizardAdditionalAccountsDeactivateTypes,
    makeSelectWizardAdditionalAccountsOfferLink,
    makeSelectWizardAdditionalAccountsSelectedTypes,
} from '../wizardAdditionalAccountsSelectors';
import { WizardAdditionalAccountsStyled } from './styled/WizardAdditionalAccountsStyled';

const WizardAdditionalAccountsSelectParams = (props: IConnectedProps & IDispatchProps) => {
    const {
        activateTypes,
        deactivateTypes,
        offerLink,
        translation,
        selected,

        saveCurrentStepState,
    } = props;

    const changeCheckboxHandler = useCallback((event: boolean, key: string) => {
        saveCurrentStepState({
            selected: {
                ...selected,
                [key]: event,
                // Если подключаем персональный счет, то:
                // 1. "Подключить" принимает значение персонального счета
                // (т.к. подключить контентный без персонального нельзя)
                // 2. "Подключение контентного счета" невозможно без персонального
                ...(key === 'isActivatePersonalAccount'
                        ? {
                            isActivateAdditionalAccounts: event,
                            isActivateContentAccount: event ? selected.isActivateContentAccount : false,
                        }
                        : {}
                ),
                // "Подключение контентного счета" невозможно без персонального
                ...(key === 'isActivateContentAccount'
                        ? {
                            isActivateAdditionalAccounts: event
                                ? true
                                : (!selected.isActivatePersonalAccount ? false : selected.isActivateAdditionalAccounts),
                            isActivatePersonalAccount: event
                                ? activateTypes.length === 2
                                : selected.isActivatePersonalAccount,
                        }
                        : {}
                ),
                ...(key === 'isActivatePersonalAccount' || key === 'isActivateContentAccount'
                        ? {
                            isDeactivateAdditionalAccounts: false,
                            isDeactivateContentAccount: false,
                            isDeactivatePersonalAccount: false,
                        }
                        : {}
                ),
                // Отключение персонального невозможно без отключения контентного
                ...(key === 'isDeactivatePersonalAccount'
                        ? {
                            isDeactivateAdditionalAccounts: event
                                ? true
                                : (!selected.isDeactivateContentAccount
                                    ? false
                                    : selected.isDeactivateAdditionalAccounts),

                            isDeactivateContentAccount: event
                                ? deactivateTypes.length === 2
                                : selected.isDeactivateContentAccount,
                        }
                        : {}
                ),
                ...(key === 'isDeactivateContentAccount'
                        ? {
                            isDeactivateAdditionalAccounts: event,
                            isDeactivatePersonalAccount: event
                                ? selected.isDeactivatePersonalAccount
                                : false,
                        }
                        : {}
                ),
                ...(key === 'isDeactivatePersonalAccount' || key === 'isDeactivateContentAccount'
                        ? {
                            isActivateAdditionalAccounts: false,
                            isActivateContentAccount: false,
                            isActivatePersonalAccount: false,
                        }
                        : {}
                ),
            },
        });

        if (!selected.isActivateAdditionalAccounts) {
            saveCurrentStepState({
                isAgreementCheckboxEnabled: false,
            });
        }
    }, [activateTypes.length, deactivateTypes.length, saveCurrentStepState, selected]);

    const changeAgreementCheckboxHandler = useCallback((event: boolean) => {
        saveCurrentStepState({
            isAgreementCheckboxEnabled: event,
        });
    }, [saveCurrentStepState]);

    return <WizardAdditionalAccountsStyled>
        <div>
            {
                activateTypes && activateTypes.length > 0 && <Fragment>
                    <RadioButton checked={selected.isActivateAdditionalAccounts}
                                 className="wizard-additional-accounts__radio-button"
                                 label={translation[EWizardAdditionalAccountsSteps.StepSelectParams].activate}
                    />
                    {
                        (activateTypes.length === 2) &&
                        <Checkbox checked={selected.isActivatePersonalAccount}
                                  className="wizard-additional-accounts__checkbox"
                                  label={activateTypes[0]?.def}
                                  onChange={e => changeCheckboxHandler(e, 'isActivatePersonalAccount')}/>
                    }
                    <Checkbox checked={selected.isActivateContentAccount}
                              className="wizard-additional-accounts__checkbox"
                              label={activateTypes[activateTypes.length === 2 ? 1 : 0]?.def}
                              onChange={e => changeCheckboxHandler(e, 'isActivateContentAccount')}
                    />
                </Fragment>
            }
            {
                deactivateTypes && deactivateTypes.length > 0 && <Fragment>
                    <RadioButton checked={selected.isDeactivateAdditionalAccounts}
                                 className="wizard-additional-accounts__radio-button"
                                 label={translation[EWizardAdditionalAccountsSteps.StepSelectParams].deactivate}
                    />
                    <Checkbox checked={selected.isDeactivatePersonalAccount}
                              className="wizard-additional-accounts__checkbox"
                              label={deactivateTypes[0]?.def}
                              onChange={e => changeCheckboxHandler(e, 'isDeactivatePersonalAccount')}
                    />
                    {(deactivateTypes.length === 2) &&
                    <Checkbox checked={selected.isDeactivateContentAccount}
                              className="wizard-additional-accounts__checkbox"
                              label={deactivateTypes[1]?.def}
                              onChange={e => changeCheckboxHandler(e, 'isDeactivateContentAccount')}
                    />
                    }
                </Fragment>
            }
            {
                selected.isActivatePersonalAccount && <div className="wizard-additional-accounts__agreement-link">
                    <Checkbox
                        onChange={changeAgreementCheckboxHandler}
                    />
                    {translation[EWizardAdditionalAccountsSteps.StepSelectParams].agreementLabel}&nbsp;
                    <LinkBlock isExternalUrl
                               target={ELinkTarget.BLANK}
                               url={offerLink}>
                        {translation[EWizardAdditionalAccountsSteps.StepSelectParams].agreementLinkLabel}
                    </LinkBlock>
                </div>
            }
        </div>
    </WizardAdditionalAccountsStyled>;
};

export interface IConnectedProps extends Pick<IWizardAdditionalAccountsReducer,
    'activateTypes' | 'deactivateTypes' | 'offerLink' | 'selected'> {
    translation: IWizardAdditionalAccountsTranslation;
}

interface IDispatchProps {
    saveCurrentStepState: (payload: TWizardAdditionalAccountsSetParamsPayload) => void;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    activateTypes: makeSelectWizardAdditionalAccountsActivateTypes(),
    deactivateTypes: makeSelectWizardAdditionalAccountsDeactivateTypes(),
    offerLink: makeSelectWizardAdditionalAccountsOfferLink(),
    translation: makeSelectTranslationWizardAdditionalAccounts(),
    selected: makeSelectWizardAdditionalAccountsSelectedTypes(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    saveCurrentStepState: (payload: TWizardAdditionalAccountsSetParamsPayload) =>
        dispatch(wizardAdditionalAccountsSetParamsAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardAdditionalAccountsSelectParams);
